import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from '@tanstack/react-router'
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  ChevronRight,
  ClockAlert,
  FileText,
  FolderClosed,
  FolderOpen,
  FolderPlus,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  useCreateFolder,
  useDeleteFolder,
  useDeleteProject,
  useDocuments,
  useFolders,
  useMoveDocumentInWorkspace,
  useDeleteDocument,
  useProjects,
  useUpdateDocument,
  useUpdateFolder,
  useUpdateProject,
  useWorkspace,
  type FolderRead,
  type WorkspaceRead,
} from '@/api/queries'
import type { DocumentSummary } from '@/api/docfiller-types'
import ErrorFallback from '@/components/ErrorFallback'
import { MoveDocumentDialog } from '@/components/projects/MoveDocumentDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { formatError } from '@/lib/format-error'
import { formatLocalDateTime } from '@/lib/format-date'
import {
  canDeleteProjects,
  canEditProjects,
  canManageFolders,
  canMoveDocuments,
} from '@/lib/workspace-roles'

const ROOT_FOLDER_KEY = '__root__'
const NO_PROJECT_KEY = '__no_project__'
const DOC_DRAG_TYPE = 'application/x-docfiller-document'

type DocumentMoveState = 'idle' | 'pending' | 'settled'
type DocumentStateFilter = 'active' | 'archived' | 'deleted' | 'expired'

interface ProjectSearch {
  folder?: string
}

function parseSearch(raw: Record<string, unknown>): ProjectSearch {
  const folder = raw.folder
  return typeof folder === 'string' && folder.length > 0 ? { folder } : {}
}

export const Route = createFileRoute(
  '/_authenticated/projects/$workspaceUuid/$projectUuid',
)({
  component: ProjectDetailPage,
  errorComponent: ErrorFallback,
  validateSearch: parseSearch,
})

export function ProjectDetailPage() {
  const { workspaceUuid, projectUuid } = Route.useParams()
  const search = useSearch({ from: Route.id })
  const navigate = useNavigate()

  const selectedFolderUuid = search.folder ?? null
  const selectedLocation =
    selectedFolderUuid === NO_PROJECT_KEY ? NO_PROJECT_KEY : selectedFolderUuid

  const { data: workspace, isLoading: wsLoading, error: wsError } = useWorkspace(
    workspaceUuid,
  )
  const { data: projects } = useProjects(workspaceUuid)
  const project = projects?.find((p) => p.uuid === projectUuid)
  const [pendingMoveDocumentIds, setPendingMoveDocumentIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [settledMoveDocumentIds, setSettledMoveDocumentIds] = useState<Set<string>>(
    () => new Set(),
  )

  const setDocumentMoveState = useCallback(
    (documentId: string, state: DocumentMoveState) => {
      setPendingMoveDocumentIds((current) => {
        const next = new Set(current)
        if (state === 'pending') {
          next.add(documentId)
        } else {
          next.delete(documentId)
        }
        return next
      })
      setSettledMoveDocumentIds((current) => {
        const next = new Set(current)
        if (state === 'settled') {
          next.add(documentId)
        } else {
          next.delete(documentId)
        }
        return next
      })
    },
    [],
  )

  const setSelectedFolder = (next: string | null) => {
    void navigate({
      to: '/projects/$workspaceUuid/$projectUuid',
      params: { workspaceUuid, projectUuid },
      search: next ? { folder: next } : {},
      replace: true,
    })
  }

  if (wsLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-8 md:px-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (wsError) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8">
        <p className="text-sm text-destructive" role="alert">
          Failed to load workspace: {formatError(wsError)}
        </p>
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8">
        <EmptyState
          icon={FolderClosed}
          title="Workspace not found"
          description="This workspace doesn't exist or you don't have access. It may have been deleted."
          action={
            <Button asChild size="sm">
              <Link to="/projects">Back to projects</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (projects && !project) {
    return (
      <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-8">
        <EmptyState
          icon={FolderClosed}
          title="Project not found"
          description="The project you're looking for doesn't exist in this workspace. It may have been deleted."
          action={
            <Button asChild size="sm">
              <Link to="/projects">Back to projects</Link>
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-8 md:px-8">
      <ProjectHeader
        workspace={workspace}
        projectUuid={projectUuid}
        projectName={project?.name ?? '…'}
      />

      <div className="grid gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <FolderTreePanel
          workspaceUuid={workspaceUuid}
          projectUuid={projectUuid}
          workspace={workspace}
          selectedFolderUuid={selectedLocation}
          onSelectFolder={setSelectedFolder}
          pendingMoveDocumentIds={pendingMoveDocumentIds}
          onDocumentMoveStateChange={setDocumentMoveState}
        />
        <DocumentListPanel
          workspaceUuid={workspaceUuid}
          projectUuid={projectUuid}
          folderUuid={selectedLocation}
          workspace={workspace}
          pendingMoveDocumentIds={pendingMoveDocumentIds}
          settledMoveDocumentIds={settledMoveDocumentIds}
          onDocumentMoveStateChange={setDocumentMoveState}
        />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Header: breadcrumb, title, settings menu (rename/delete).
// ---------------------------------------------------------------------------

function ProjectHeader({
  workspace,
  projectUuid,
  projectName,
}: {
  workspace: WorkspaceRead
  projectUuid: string
  projectName: string
}) {
  const canEdit = canEditProjects(workspace.role)
  const canDelete = canDeleteProjects(workspace.role)
  const navigate = useNavigate()

  const updateProject = useUpdateProject(workspace.uuid)
  const deleteProject = useDeleteProject(workspace.uuid)
  const [renameOpen, setRenameOpen] = useState(false)
  const [name, setName] = useState(projectName)
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Sync local rename state when the project name updates externally.
  if (renameOpen && name === '…' && projectName !== '…') {
    setName(projectName)
  }

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed || trimmed === projectName) {
      setRenameOpen(false)
      return
    }
    try {
      await updateProject.mutateAsync({
        projectUuid,
        body: { name: trimmed },
      })
      toast.success('Project renamed')
      setRenameOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const handleDelete = async () => {
    try {
      await deleteProject.mutateAsync(projectUuid)
      toast.success(`Project "${projectName}" deleted`)
      void navigate({ to: '/projects' })
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <>
      <div className="space-y-1">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1 text-xs text-muted-foreground"
        >
          <Link
            to="/projects"
            className="rounded px-1 py-0.5 hover:bg-muted/60 hover:text-foreground"
          >
            Projects
          </Link>
          <ChevronRight className="size-3" aria-hidden />
          <span>{workspace.name}</span>
        </nav>
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <FolderOpen className="size-5 text-primary" aria-hidden />
              {projectName}
            </span>
          }
          description={`Documents and folders in ${workspace.name}.`}
          actions={
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/projects">
                  <ArrowLeft />
                  Back
                </Link>
              </Button>
              {(canEdit || canDelete) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon-sm"
                      aria-label="Project actions"
                    >
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                        <Pencil className="size-4" />
                        Rename
                      </DropdownMenuItem>
                    )}
                    {canEdit && canDelete && <DropdownMenuSeparator />}
                    {canDelete && (
                      <DropdownMenuItem
                        variant="destructive"
                        onSelect={() => setDeleteOpen(true)}
                      >
                        <Trash2 className="size-4" />
                        Delete project
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          }
        />
      </div>

      {/* Rename dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rename-project-name">Name</Label>
              <Input
                id="rename-project-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameOpen(false)}
                disabled={updateProject.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateProject.isPending || !name.trim()}>
                {updateProject.isPending && <Loader2 className="animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete project?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Documents inside the project keep their workspace access. The
            project + its folders are soft-archived and can be restored from
            the audit log.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleteProject.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteProject.isPending}
            >
              {deleteProject.isPending && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ---------------------------------------------------------------------------
// Folder tree panel — keyboard nav: up/down moves between rows, Enter selects,
// right arrow expands, left arrow collapses. Drag-and-drop is not in MVP.
// ---------------------------------------------------------------------------

interface TreeNode {
  folder: FolderRead | null // null = root row
  children: TreeNode[]
  depth: number
}

// Walk parent_folder_uuid links from ``candidateUuid`` upward, returning true
// if ``ancestorUuid`` (inclusive of the candidate itself) is anywhere in the
// chain. Cycle-safe via a visited set.
function isFolderInSubtree(
  folders: FolderRead[],
  candidateUuid: string,
  ancestorUuid: string,
): boolean {
  const byUuid = new Map(folders.map((f) => [f.uuid, f]))
  const visited = new Set<string>()
  let current: string | null = candidateUuid
  while (current && !visited.has(current)) {
    if (current === ancestorUuid) return true
    visited.add(current)
    current = byUuid.get(current)?.parent_folder_uuid ?? null
  }
  return false
}

function buildFolderTree(folders: FolderRead[]): TreeNode {
  const byParent = new Map<string | null, FolderRead[]>()
  for (const f of folders) {
    const key = f.parent_folder_uuid ?? null
    const list = byParent.get(key) ?? []
    list.push(f)
    byParent.set(key, list)
  }
  // Sort each level alphabetically — stable, predictable order.
  for (const list of byParent.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name))
  }
  const build = (parent: string | null, depth: number): TreeNode[] => {
    const direct = byParent.get(parent) ?? []
    return direct.map((f) => ({
      folder: f,
      children: build(f.uuid, depth + 1),
      depth,
    }))
  }
  return { folder: null, children: build(null, 0), depth: -1 }
}

function flattenTree(
  node: TreeNode,
  expanded: Set<string>,
  out: TreeNode[] = [],
): TreeNode[] {
  if (node.folder !== null) {
    out.push(node)
    if (!expanded.has(node.folder.uuid)) return out
  }
  for (const child of node.children) {
    flattenTree(child, expanded, out)
  }
  return out
}

function FolderTreePanel({
  workspaceUuid,
  projectUuid,
  workspace,
  selectedFolderUuid,
  onSelectFolder,
  pendingMoveDocumentIds,
  onDocumentMoveStateChange,
}: {
  workspaceUuid: string
  projectUuid: string
  workspace: WorkspaceRead
  selectedFolderUuid: string | null
  onSelectFolder: (next: string | null) => void
  pendingMoveDocumentIds: ReadonlySet<string>
  onDocumentMoveStateChange: (
    documentId: string,
    state: DocumentMoveState,
  ) => void
}) {
  const { data: folders, isLoading, error } = useFolders(workspaceUuid, projectUuid)
  const canManage = canManageFolders(workspace.role)
  const createFolder = useCreateFolder(workspaceUuid, projectUuid)
  const updateFolder = useUpdateFolder(workspaceUuid, projectUuid)
  const deleteFolder = useDeleteFolder(workspaceUuid, projectUuid)
  const moveDocument = useMoveDocumentInWorkspace(workspaceUuid)
  const [dropTarget, setDropTarget] = useState<string | null>(null)

  const tree = useMemo(() => buildFolderTree(folders ?? []), [folders])

  // User-toggled expansion state. Folders not in this set may still
  // render expanded if they're ancestors of the selected folder — see
  // ``effectiveExpanded`` below for the derived merge.
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  // Derive the actual "expanded" set at render time: anything the user
  // clicked open, plus every ancestor of the currently-selected folder
  // so the active row is always on-screen. Computing this in render
  // (rather than syncing state via useEffect) avoids cascading-render
  // lint errors and is cheaper for shallow trees.
  const effectiveExpanded = useMemo(() => {
    const next = new Set(expanded)
    if (selectedFolderUuid && folders) {
      let current: FolderRead | undefined = folders.find(
        (f) => f.uuid === selectedFolderUuid,
      )
      while (current?.parent_folder_uuid) {
        next.add(current.parent_folder_uuid)
        current = folders.find((f) => f.uuid === current!.parent_folder_uuid)
      }
    }
    return next
  }, [expanded, selectedFolderUuid, folders])

  const flatRows: TreeNode[] = useMemo(
    () => flattenTree(tree, effectiveExpanded),
    [tree, effectiveExpanded],
  )

  const [createOpen, setCreateOpen] = useState(false)
  const [createParent, setCreateParent] = useState<FolderRead | null>(null)
  const [newName, setNewName] = useState('')

  const [renameTarget, setRenameTarget] = useState<FolderRead | null>(null)
  const [renameName, setRenameName] = useState('')

  const [deleteTarget, setDeleteTarget] = useState<FolderRead | null>(null)

  const moveDroppedDocument = async (
    e: React.DragEvent,
    target: { projectUuid: string | null; folderUuid: string | null },
  ) => {
    e.preventDefault()
    setDropTarget(null)
    const documentId = e.dataTransfer.getData(DOC_DRAG_TYPE)
    if (!documentId) return
    if (pendingMoveDocumentIds.has(documentId)) return
    try {
      onDocumentMoveStateChange(documentId, 'pending')
      await moveDocument.mutateAsync({
        documentId,
        body: {
          project_uuid: target.projectUuid,
          folder_uuid: target.folderUuid,
        },
      })
      toast.success('Document moved')
      onDocumentMoveStateChange(documentId, 'settled')
    } catch (err) {
      toast.error(formatError(err))
      onDocumentMoveStateChange(documentId, 'idle')
    }
  }

  const openCreate = (parent: FolderRead | null) => {
    setCreateParent(parent)
    setNewName('')
    setCreateOpen(true)
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = newName.trim()
    if (!trimmed) return
    try {
      const created = await createFolder.mutateAsync({
        name: trimmed,
        parent_folder_uuid: createParent?.uuid ?? null,
      })
      // Auto-select the new folder so the doc panel scopes to it.
      onSelectFolder(created.uuid)
      toast.success(`Folder "${trimmed}" created`)
      setCreateOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const openRename = (folder: FolderRead) => {
    setRenameTarget(folder)
    setRenameName(folder.name)
  }

  const handleRename = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!renameTarget) return
    const trimmed = renameName.trim()
    if (!trimmed || trimmed === renameTarget.name) {
      setRenameTarget(null)
      return
    }
    try {
      await updateFolder.mutateAsync({
        folderUuid: renameTarget.uuid,
        body: { name: trimmed },
      })
      toast.success('Folder renamed')
      setRenameTarget(null)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    // Capture before the mutation lands so we can compare against the
    // pre-delete folder list — the optimistic update strips descendants
    // immediately, which would defeat the ancestor walk.
    const clearSelection =
      selectedFolderUuid !== null &&
      isFolderInSubtree(folders ?? [], selectedFolderUuid, deleteTarget.uuid)
    try {
      await deleteFolder.mutateAsync(deleteTarget.uuid)
      toast.success(`Folder "${deleteTarget.name}" deleted`)
      if (clearSelection) {
        onSelectFolder(null)
      }
      setDeleteTarget(null)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const handleRowKeyDown = (
    e: React.KeyboardEvent<HTMLLIElement>,
    index: number,
    node: TreeNode,
  ) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const next = e.currentTarget.parentElement?.children[
        index + 1
      ] as HTMLElement | undefined
      next?.querySelector<HTMLButtonElement>('button[data-tree-row]')?.focus()
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const prev = e.currentTarget.parentElement?.children[
        index - 1
      ] as HTMLElement | undefined
      prev?.querySelector<HTMLButtonElement>('button[data-tree-row]')?.focus()
    } else if (e.key === 'ArrowRight' && node.folder && node.children.length > 0) {
      e.preventDefault()
      setExpanded((prev) => {
        const next = new Set(prev)
        next.add(node.folder!.uuid)
        return next
      })
    } else if (e.key === 'ArrowLeft' && node.folder) {
      e.preventDefault()
      if (effectiveExpanded.has(node.folder.uuid)) {
        setExpanded((prev) => {
          const next = new Set(prev)
          next.delete(node.folder!.uuid)
          return next
        })
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (node.folder) onSelectFolder(node.folder.uuid)
    }
  }

  return (
    <Card className="self-start" tone="muted">
      <CardHeader className="flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm font-semibold">Folders</CardTitle>
        {canManage && (
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => openCreate(null)}
            aria-label="New top-level folder"
            data-testid="new-folder-btn"
          >
            <FolderPlus />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-1 pt-0">
        {isLoading && (
          <div className="flex items-center gap-2 py-2 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Loading folders…
          </div>
        )}
        {error && (
          <p className="text-xs text-destructive" role="alert">
            {formatError(error)}
          </p>
        )}
        {!isLoading && !error && (
          <ul
            role="tree"
            aria-label="Project folders"
            className="space-y-0.5"
            data-testid="folder-tree"
          >
            <li
              onDragOver={(e) => {
                e.preventDefault()
                setDropTarget(NO_PROJECT_KEY)
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) =>
                void moveDroppedDocument(e, {
                  projectUuid: null,
                  folderUuid: null,
                })
              }
            >
              <FolderRow
                label="No project"
                icon={FolderClosed}
                selected={selectedFolderUuid === NO_PROJECT_KEY}
                onSelect={() => onSelectFolder(NO_PROJECT_KEY)}
                depth={0}
                dropping={dropTarget === NO_PROJECT_KEY}
                data-folder-uuid={NO_PROJECT_KEY}
              />
            </li>
            <li
              onDragOver={(e) => {
                e.preventDefault()
                setDropTarget(ROOT_FOLDER_KEY)
              }}
              onDragLeave={() => setDropTarget(null)}
              onDrop={(e) =>
                void moveDroppedDocument(e, {
                  projectUuid,
                  folderUuid: null,
                })
              }
              onKeyDown={(e) => {
                // The root "All documents" row participates in the same
                // up/down focus traversal as the folder rows, even though
                // it isn't a TreeNode.
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  const next = e.currentTarget.nextElementSibling as HTMLElement | null
                  next
                    ?.querySelector<HTMLButtonElement>('button[data-tree-row]')
                    ?.focus()
                } else if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelectFolder(null)
                }
              }}
            >
              <FolderRow
                label="All documents"
                icon={FolderOpen}
                selected={selectedFolderUuid === null}
                onSelect={() => onSelectFolder(null)}
                depth={0}
                dropping={dropTarget === ROOT_FOLDER_KEY}
                data-folder-uuid={ROOT_FOLDER_KEY}
              />
            </li>
            {flatRows.length === 0 && (
              <li
                className={cn(
                  'px-2 py-2 text-xs text-muted-foreground',
                  'rounded-md',
                )}
              >
                {canManage
                  ? 'No folders yet. Create one to organise documents.'
                  : 'No folders yet.'}
              </li>
            )}
            {flatRows.map((node, index) => {
              if (!node.folder) return null
              const isExpanded = effectiveExpanded.has(node.folder.uuid)
              const hasChildren = node.children.length > 0
              return (
                <li
                  key={node.folder.uuid}
                  role="treeitem"
                  aria-level={node.depth + 1}
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  aria-selected={selectedFolderUuid === node.folder.uuid}
                  onKeyDown={(e) => handleRowKeyDown(e, index, node)}
                >
                  <FolderRow
                    label={node.folder.name}
                    icon={isExpanded ? FolderOpen : FolderClosed}
                    selected={selectedFolderUuid === node.folder.uuid}
                    onSelect={() => onSelectFolder(node.folder!.uuid)}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setDropTarget(node.folder!.uuid)
                    }}
                    onDragLeave={() => setDropTarget(null)}
                    onDrop={(e) =>
                      void moveDroppedDocument(e, {
                        projectUuid,
                        folderUuid: node.folder!.uuid,
                      })
                    }
                    dropping={dropTarget === node.folder.uuid}
                    onToggleExpand={
                      hasChildren
                        ? () => {
                            setExpanded((prev) => {
                              const next = new Set(prev)
                              if (next.has(node.folder!.uuid)) {
                                next.delete(node.folder!.uuid)
                              } else {
                                next.add(node.folder!.uuid)
                              }
                              return next
                            })
                          }
                        : undefined
                    }
                    expanded={isExpanded}
                    hasChildren={hasChildren}
                    depth={node.depth}
                    data-folder-uuid={node.folder.uuid}
                  >
                    {canManage && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            className="size-6 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                            aria-label={`Folder actions for ${node.folder.name}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="size-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => openCreate(node.folder)}
                          >
                            <FolderPlus className="size-4" />
                            New subfolder
                          </DropdownMenuItem>
                          <DropdownMenuItem onSelect={() => openRename(node.folder!)}>
                            <Pencil className="size-4" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            variant="destructive"
                            onSelect={() => setDeleteTarget(node.folder)}
                          >
                            <Trash2 className="size-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </FolderRow>
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>

      {/* Create folder dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {createParent ? `New folder in "${createParent.name}"` : 'New folder'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="new-folder-name">Name</Label>
              <Input
                id="new-folder-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOpen(false)}
                disabled={createFolder.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createFolder.isPending || !newName.trim()}>
                {createFolder.isPending && <Loader2 className="animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={renameTarget !== null} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename folder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRename} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="rename-folder-name">Name</Label>
              <Input
                id="rename-folder-name"
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameTarget(null)}
                disabled={updateFolder.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateFolder.isPending || !renameName.trim()}>
                {updateFolder.isPending && <Loader2 className="animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete folder confirm */}
      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Delete folder &quot;{deleteTarget?.name}&quot;?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This folder and its subfolders will be hidden from the tree.
            Documents inside them stay in the project and remain reachable
            from the project&apos;s document list, but they will no longer
            be grouped under a folder.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteFolder.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteFolder.isPending}
            >
              {deleteFolder.isPending && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

interface FolderRowProps {
  label: string
  icon: React.ComponentType<{ className?: string }>
  selected: boolean
  onSelect: () => void
  onToggleExpand?: () => void
  expanded?: boolean
  hasChildren?: boolean
  depth: number
  dropping?: boolean
  onDragOver?: (e: React.DragEvent<HTMLDivElement>) => void
  onDragLeave?: (e: React.DragEvent<HTMLDivElement>) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>) => void
  children?: React.ReactNode
  'data-folder-uuid'?: string
}

function FolderRow({
  label,
  icon: Icon,
  selected,
  onSelect,
  onToggleExpand,
  expanded,
  hasChildren,
  depth,
  dropping = false,
  onDragOver,
  onDragLeave,
  onDrop,
  children,
  ...rest
}: FolderRowProps) {
  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      data-folder-uuid={rest['data-folder-uuid']}
      className={cn(
        'group flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm',
        'transition-colors duration-100',
        'hover:bg-muted/60',
        selected && 'bg-primary-soft/70 text-primary-soft-foreground font-medium',
        dropping && 'ring-2 ring-primary/50 bg-primary-soft/70',
      )}
      style={{ paddingLeft: `${depth * 14 + 8}px` }}
    >
      {onToggleExpand ? (
        <button
          type="button"
          aria-label={expanded ? 'Collapse folder' : 'Expand folder'}
          className="flex size-4 shrink-0 items-center justify-center rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
          onClick={(e) => {
            e.stopPropagation()
            onToggleExpand()
          }}
        >
          <ChevronRight
            className={cn(
              'size-3 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
        </button>
      ) : (
        <span
          aria-hidden
          className={cn('inline-block size-4 shrink-0', hasChildren && 'opacity-40')}
        />
      )}
      <button
        type="button"
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
        }}
        data-tree-row
        data-folder-uuid={rest['data-folder-uuid']}
        className="flex min-w-0 flex-1 items-center gap-1.5 rounded-sm text-left focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30"
        aria-current={selected ? 'true' : undefined}
      >
        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </button>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Document list panel — scoped to (workspace, project, optional folder).
// ---------------------------------------------------------------------------

function DocumentListPanel({
  workspaceUuid,
  projectUuid,
  folderUuid,
  workspace,
  pendingMoveDocumentIds,
  settledMoveDocumentIds,
  onDocumentMoveStateChange,
}: {
  workspaceUuid: string
  projectUuid: string
  folderUuid: string | null
  workspace: WorkspaceRead
  pendingMoveDocumentIds: ReadonlySet<string>
  settledMoveDocumentIds: ReadonlySet<string>
  onDocumentMoveStateChange: (
    documentId: string,
    state: DocumentMoveState,
  ) => void
}) {
  const isNoProject = folderUuid === NO_PROJECT_KEY
  const [stateFilter, setStateFilter] = useState<DocumentStateFilter>('active')
  const { data: documents, isLoading, isFetching, error } = useDocuments(
    isNoProject
      ? { workspaceUuid, noProject: true, state: stateFilter }
      : {
          workspaceUuid,
          projectUuid,
          folderUuid: folderUuid ?? undefined,
          state: stateFilter,
        },
  )
  const canMove = canMoveDocuments(workspace.role)
  const moveDocument = useMoveDocumentInWorkspace(workspaceUuid)
  const [dragOverDoc, setDragOverDoc] = useState<string | null>(null)

  useEffect(() => {
    if (isFetching || !documents || settledMoveDocumentIds.size === 0) return
    for (const doc of documents) {
      if (settledMoveDocumentIds.has(doc.uuid)) {
        onDocumentMoveStateChange(doc.uuid, 'idle')
      }
    }
  }, [documents, isFetching, onDocumentMoveStateChange, settledMoveDocumentIds])

  const reorderDocument = async (draggedUuid: string, beforeUuid: string | null) => {
    if (!documents || draggedUuid === beforeUuid) return
    if (pendingMoveDocumentIds.has(draggedUuid)) return
    const withoutDragged = documents
      .filter((doc) => doc.uuid !== draggedUuid)
      .map((doc) => doc.uuid)
    const beforeIndex = beforeUuid ? withoutDragged.indexOf(beforeUuid) : -1
    const order =
      beforeIndex >= 0
        ? [
            ...withoutDragged.slice(0, beforeIndex),
            draggedUuid,
            ...withoutDragged.slice(beforeIndex),
          ]
        : [...withoutDragged, draggedUuid]
    try {
      onDocumentMoveStateChange(draggedUuid, 'pending')
      await moveDocument.mutateAsync({
        documentId: draggedUuid,
        body: {
          project_uuid: isNoProject ? null : projectUuid,
          folder_uuid: isNoProject ? null : folderUuid,
          order,
        },
      })
      toast.success('Document order saved')
      onDocumentMoveStateChange(draggedUuid, 'idle')
    } catch (err) {
      toast.error(formatError(err))
      onDocumentMoveStateChange(draggedUuid, 'idle')
    } finally {
      setDragOverDoc(null)
    }
  }

  const listTitle = isNoProject
    ? 'No Project documents'
    : folderUuid
      ? 'Folder documents'
      : 'Project root documents'

  return (
    <Card>
      <CardHeader>
        <SectionHeader
          title={listTitle}
          description={
            documents && documents.length > 0
              ? `${documents.length} document${documents.length === 1 ? '' : 's'} in this view.`
              : undefined
          }
          actions={
            <div className="flex flex-wrap items-center gap-2">
              <DocumentStateFilterControl
                value={stateFilter}
                onChange={setStateFilter}
              />
              <Button asChild size="sm">
                <Link
                  to="/docfiller"
                  search={{
                    workspaceUuid,
                    ...(isNoProject
                      ? {}
                      : {
                          projectUuid,
                          ...(folderUuid ? { folderUuid } : {}),
                        }),
                  }}
                  title={
                    isNoProject
                      ? 'Upload a document without assigning it to a project.'
                      : folderUuid
                      ? 'Upload a document into this folder.'
                      : 'Upload a document into this project.'
                  }
                >
                  <Plus />
                  {isNoProject
                    ? 'Upload without project'
                    : folderUuid
                      ? 'Upload to folder'
                      : 'Upload to project'}
                </Link>
              </Button>
            </div>
          }
        />
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive" role="alert">
            {formatError(error)}
          </p>
        )}
        {!isLoading && !error && documents && documents.length === 0 && (
          <EmptyState
            icon={FileText}
            variant="bare"
            title={
              isNoProject
                ? 'No documents without a project'
                : folderUuid
                  ? 'This folder is empty'
                  : 'No documents in this project root'
            }
            description={
              isNoProject
                ? 'Drag documents here to remove them from a project.'
                : folderUuid
                ? 'Move documents into this folder from the project root or another folder.'
                : 'Upload a document or move one in from another project.'
            }
            action={
              <Button asChild size="sm" variant="outline">
                <Link
                  to="/docfiller"
                  search={{
                    workspaceUuid,
                    ...(isNoProject
                      ? {}
                      : {
                          projectUuid,
                          ...(folderUuid ? { folderUuid } : {}),
                        }),
                  }}
                >
                  <Plus />
                  {isNoProject
                    ? 'Upload without project'
                    : folderUuid
                      ? 'Upload to folder'
                      : 'Upload to project'}
                </Link>
              </Button>
            }
          />
        )}
        {!isLoading && !error && documents && documents.length > 0 && (
          <ul className="divide-y divide-border" data-testid="project-documents">
            {documents.map((doc) => (
              <DocumentRow
                key={doc.uuid}
                doc={doc}
                workspaceUuid={workspaceUuid}
                projectUuid={isNoProject ? null : projectUuid}
                folderUuid={isNoProject ? null : folderUuid}
                canMove={canMove}
                isMovePending={
                  pendingMoveDocumentIds.has(doc.uuid) ||
                  settledMoveDocumentIds.has(doc.uuid)
                }
                onMoveStateChange={onDocumentMoveStateChange}
                dragOver={dragOverDoc === doc.uuid}
                onDragStart={(e) => {
                  if (pendingMoveDocumentIds.has(doc.uuid)) {
                    e.preventDefault()
                    return
                  }
                  e.dataTransfer.setData(DOC_DRAG_TYPE, doc.uuid)
                  e.dataTransfer.effectAllowed = 'move'
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOverDoc(doc.uuid)
                }}
                onDragLeave={() => setDragOverDoc(null)}
                onDrop={(e) => {
                  e.preventDefault()
                  const draggedUuid = e.dataTransfer.getData(DOC_DRAG_TYPE)
                  if (draggedUuid) void reorderDocument(draggedUuid, doc.uuid)
                }}
              />
            ))}
            <li
              className="py-2"
              onDragOver={(e) => {
                e.preventDefault()
                setDragOverDoc('__end__')
              }}
              onDragLeave={() => setDragOverDoc(null)}
              onDrop={(e) => {
                e.preventDefault()
                const draggedUuid = e.dataTransfer.getData(DOC_DRAG_TYPE)
                if (draggedUuid) void reorderDocument(draggedUuid, null)
              }}
            >
              <div
                className={cn(
                  'rounded-md border border-dashed border-transparent px-3 py-2 text-center text-xs text-muted-foreground',
                  dragOverDoc === '__end__' && 'border-primary bg-primary-soft/50',
                )}
              >
                Drop here to place at the end
              </div>
            </li>
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

const DOCUMENT_STATE_OPTIONS: {
  value: DocumentStateFilter
  label: string
  icon: React.ComponentType<{ className?: string }>
}[] = [
  { value: 'active', label: 'Active', icon: FileText },
  { value: 'archived', label: 'Archived', icon: Archive },
  { value: 'deleted', label: 'Deleted', icon: Trash2 },
  { value: 'expired', label: 'Expired', icon: ClockAlert },
]

function DocumentStateFilterControl({
  value,
  onChange,
}: {
  value: DocumentStateFilter
  onChange: (next: DocumentStateFilter) => void
}) {
  return (
    <div className="inline-flex max-w-full overflow-x-auto rounded-md border border-border bg-card p-0.5">
      {DOCUMENT_STATE_OPTIONS.map((option) => {
        const Icon = option.icon
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            aria-pressed={selected}
            className={cn(
              'inline-flex h-7 shrink-0 items-center gap-1.5 rounded px-2 text-xs font-medium text-muted-foreground',
              selected && 'bg-primary-soft text-primary-soft-foreground',
            )}
          >
            <Icon className="size-3.5" aria-hidden />
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

function documentTitle(doc: DocumentSummary): string {
  return doc.display_name?.trim() || doc.source_filename
}

function DocumentStatusIcons({ doc }: { doc: DocumentSummary }) {
  const chips = [
    doc.is_archived
      ? { key: 'archived', label: 'Archived', icon: Archive, tone: 'neutral' as const }
      : null,
    doc.is_deleted
      ? { key: 'deleted', label: 'Deleted', icon: Trash2, tone: 'danger' as const }
      : null,
    doc.is_expired
      ? { key: 'expired', label: 'Expired', icon: ClockAlert, tone: 'warning' as const }
      : null,
  ].filter((chip): chip is NonNullable<typeof chip> => chip !== null)

  return (
    <div className="hidden shrink-0 flex-wrap items-center gap-1 sm:flex">
      <Badge tone="outline" size="xs">
        {doc.status}
      </Badge>
      {chips.map((chip) => {
        const Icon = chip.icon
        return (
          <Badge key={chip.key} tone={chip.tone} size="xs" title={chip.label}>
            <Icon className="size-3" aria-hidden />
            <span className="sr-only md:not-sr-only">{chip.label}</span>
          </Badge>
        )
      })}
    </div>
  )
}

function DocumentRow({
  doc,
  workspaceUuid,
  projectUuid,
  folderUuid,
  canMove,
  isMovePending,
  onMoveStateChange,
  dragOver,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
}: {
  doc: DocumentSummary
  workspaceUuid: string
  projectUuid: string | null
  folderUuid: string | null
  canMove: boolean
  isMovePending: boolean
  onMoveStateChange: (documentId: string, state: DocumentMoveState) => void
  dragOver: boolean
  onDragStart: (e: React.DragEvent<HTMLLIElement>) => void
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void
  onDragLeave: (e: React.DragEvent<HTMLLIElement>) => void
  onDrop: (e: React.DragEvent<HTMLLIElement>) => void
}) {
  const [moveOpen, setMoveOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [renameName, setRenameName] = useState(documentTitle(doc))
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const updateDocument = useUpdateDocument(doc.uuid)
  const deleteDocument = useDeleteDocument()
  const effectiveCanMove = canMove && !isMovePending
  const suppressLinkActivation = isMovePending || isDragging
  const title = documentTitle(doc)
  const showSourceFilename = doc.source_filename !== title

  const rename = async () => {
    const trimmed = renameName.trim()
    if (!trimmed) return
    try {
      await updateDocument.mutateAsync({ display_name: trimmed })
      toast.success('Document renamed')
      setRenameOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const setArchived = async (archived: boolean) => {
    try {
      await updateDocument.mutateAsync({ archived })
      toast.success(archived ? 'Document archived' : 'Document restored')
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  const remove = async () => {
    try {
      await deleteDocument.mutateAsync(doc.uuid)
      toast.success('Document deleted')
      setDeleteOpen(false)
    } catch (err) {
      toast.error(formatError(err))
    }
  }

  return (
    <li
      draggable={effectiveCanMove}
      onDragStart={(e) => {
        onDragStart(e)
        if (!e.defaultPrevented) setIsDragging(true)
      }}
      onDragEnd={() => setIsDragging(false)}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={(e) => {
        setIsDragging(false)
        onDrop(e)
      }}
      aria-disabled={isMovePending || undefined}
      data-testid={`project-document-row-${doc.uuid}`}
      className={cn(
        'flex items-center justify-between gap-3 py-2.5',
        effectiveCanMove && 'cursor-grab active:cursor-grabbing',
        isMovePending &&
          'cursor-not-allowed opacity-50 grayscale transition-opacity',
        dragOver && 'rounded-md bg-primary-soft/60 ring-2 ring-primary/40',
      )}
    >
      <Link
        to="/docfiller/$documentId"
        params={{ documentId: doc.uuid }}
        draggable={false}
        aria-disabled={suppressLinkActivation || undefined}
        tabIndex={suppressLinkActivation ? -1 : undefined}
        onClick={(e) => {
          if (suppressLinkActivation) e.preventDefault()
        }}
        onContextMenu={(e) => {
          if (effectiveCanMove || suppressLinkActivation) e.preventDefault()
        }}
        className={cn(
          'group flex min-w-0 flex-1 items-center gap-3 rounded-md p-1.5 hover:bg-muted/60',
          suppressLinkActivation && 'pointer-events-none',
        )}
        style={{ WebkitUserDrag: 'none' } as React.CSSProperties}
      >
        <span
          aria-hidden
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary-soft-foreground"
        >
          <FileText className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {title}
          </p>
          {showSourceFilename && (
            <p className="truncate text-xs text-muted-foreground" title={doc.source_filename}>
              {doc.source_filename}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Updated {formatLocalDateTime(doc.updated_at)}
          </p>
        </div>
        <DocumentStatusIcons doc={doc} />
      </Link>
      {canMove && (
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMoveOpen(true)}
            disabled={isMovePending}
            aria-label={`Move ${title}`}
          >
            Move
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Document actions for ${title}`}
                disabled={
                  isMovePending ||
                  updateDocument.isPending ||
                  deleteDocument.isPending ||
                  Boolean(doc.is_deleted)
                }
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onSelect={() => {
                  setRenameName(title)
                  setRenameOpen(true)
                }}
              >
                <Pencil className="size-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => void setArchived(!doc.is_archived)}>
                {doc.is_archived ? (
                  <ArchiveRestore className="size-4" />
                ) : (
                  <Archive className="size-4" />
                )}
                {doc.is_archived ? 'Restore from archive' : 'Archive'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setDeleteOpen(true)}>
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      <MoveDocumentDialog
        open={moveOpen}
        onOpenChange={setMoveOpen}
        documentId={doc.uuid}
        documentName={title}
        workspaceUuid={workspaceUuid}
        currentProjectUuid={projectUuid}
        currentFolderUuid={folderUuid}
        onMoveStateChange={onMoveStateChange}
      />
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename document</DialogTitle>
          </DialogHeader>
          <Input
            value={renameName}
            onChange={(e) => setRenameName(e.target.value)}
            aria-label="Document name"
            maxLength={255}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void rename()} disabled={updateDocument.isPending || !renameName.trim()}>
              {updateDocument.isPending && <Loader2 className="animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete document?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This removes {title} from active file views.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void remove()} disabled={deleteDocument.isPending}>
              {deleteDocument.isPending && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  )
}
